import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

import GlassLayout from "layouts/authentication/components/GlassLayout";
import { API_BASE } from "config/api";

function Basic() {
  const [mode, setMode] = useState("email"); // "email" | "phone"
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.from === "logout") logout();
  }, [location.state, logout]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      // Build payload — send only the relevant identifier so the backend's
      // first-match-wins logic stays predictable.
      const payload =
        mode === "email"
          ? { email: email.trim(), password }
          : { phone: normalizePhone(phone), password };

      const res = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json();

      if (res.ok) {
        if (rememberMe) localStorage.setItem("rememberMe", "true");
        else localStorage.removeItem("rememberMe");
        login(result.user, result.token);
      } else {
        const apiError =
          result?.message ||
          result?.errors?.email?.[0] ||
          result?.errors?.phone?.[0] ||
          "Login failed. Please check your credentials and try again.";
        setError(apiError);
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("We couldn't reach the server. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <GlassLayout maxWidth="sm" eyebrow="Welcome back — sign in to manage your church.">
      <Box sx={{ p: { xs: 3, sm: 4 } }}>
        <Typography variant="h4" fontWeight={700} textAlign="center">
          Sign in
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ mt: 0.5, mb: 3 }}
        >
          Use your email or phone number
        </Typography>

        <Tabs
          value={mode}
          onChange={(_, v) => {
            setMode(v);
            setError("");
          }}
          variant="fullWidth"
          sx={{
            mb: 3,
            minHeight: 40,
            borderRadius: 2,
            bgcolor: "rgba(0,0,0,0.04)",
            p: 0.5,
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              minHeight: 36,
              borderRadius: 1.5,
            },
            "& .Mui-selected": {
              bgcolor: "background.paper",
              boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
            },
          }}
        >
          <Tab
            value="email"
            label="Email"
            icon={<Icon fontSize="small">alternate_email</Icon>}
            iconPosition="start"
          />
          <Tab
            value="phone"
            label="Phone"
            icon={<Icon fontSize="small">phone</Icon>}
            iconPosition="start"
          />
        </Tabs>

        <Box component="form" role="form" onSubmit={handleLogin}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {mode === "email" ? (
            <TextField
              type="email"
              label="Email address"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon fontSize="small">mail</Icon>
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <Box sx={{ mb: 2 }}>
              <TextField
                type="tel"
                label="Phone number"
                placeholder="+233 24 000 0000"
                fullWidth
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                autoComplete="tel"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon fontSize="small">phone_iphone</Icon>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                Include your country code (e.g. +233 for Ghana).
              </Typography>
            </Box>
          )}

          <TextField
            type={showPassword ? "text" : "password"}
            label="Password"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            sx={{ mb: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon fontSize="small">lock</Icon>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Icon
                    fontSize="small"
                    sx={{ cursor: "pointer", color: "text.secondary" }}
                    onClick={() => setShowPassword((s) => !s)}
                  >
                    {showPassword ? "visibility_off" : "visibility"}
                  </Icon>
                </InputAdornment>
              ),
            }}
          />

          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ ml: -1, mb: 1 }}
          >
            <Box display="flex" alignItems="center">
              <Switch
                checked={rememberMe}
                onChange={() => setRememberMe(!rememberMe)}
                inputProps={{ "aria-label": "Remember me" }}
              />
              <Typography
                variant="button"
                color="text.secondary"
                onClick={() => setRememberMe(!rememberMe)}
                sx={{ cursor: "pointer", userSelect: "none", ml: -0.5 }}
              >
                Remember me
              </Typography>
            </Box>
            <Typography
              component={Link}
              to="/authentication/forgot-password"
              variant="caption"
              color="primary"
              fontWeight={600}
              sx={{ textDecoration: "none" }}
            >
              Forgot password?
            </Typography>
          </Box>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={isLoading}
            startIcon={
              isLoading ? <CircularProgress size={18} color="inherit" /> : <Icon>login</Icon>
            }
            sx={{ mt: 2, borderRadius: 2, textTransform: "none", fontWeight: 700 }}
          >
            {isLoading ? "Signing in…" : "Sign in"}
          </Button>

          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 3 }}>
            Don&apos;t have an account?{" "}
            <Typography
              component={Link}
              to="/authentication/sign-up"
              variant="body2"
              color="primary"
              fontWeight={700}
              sx={{ textDecoration: "none", display: "inline" }}
            >
              Sign up
            </Typography>
          </Typography>
        </Box>
      </Box>
    </GlassLayout>
  );
}

/**
 * Normalize a phone number entered in the login form so it matches the
 * stored Member.phone format ("+<dial><digits>"). Tolerates spaces,
 * dashes, parentheses, and a missing "+".
 */
function normalizePhone(raw) {
  const trimmed = (raw || "").trim();
  if (!trimmed) return trimmed;
  // Keep digits + leading "+"
  const cleaned = trimmed.replace(/[^\d+]/g, "");
  if (cleaned.startsWith("+")) return cleaned;
  // Assume Ghana if the user typed a local 0-prefixed number
  if (cleaned.startsWith("0")) return "+233" + cleaned.slice(1);
  return "+" + cleaned;
}

export default Basic;
